import React, { useState, useEffect, useCallback, memo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Alert,
  CircularProgress,
  Paper
} from '@mui/material';
import { Edit as EditIcon } from '@mui/icons-material';
import type { DoctorShiftRule, RuleType, TimeCondition } from '../../../../domain/doctorShiftRule';
import { formatTimeCondition, RULE_TYPE_LABELS } from '../../../../domain/doctorShiftRule';
import TimeConditionForm from './TimeConditionForm';

interface DoctorShiftRuleEditModalProps {
  open: boolean;
  rule: DoctorShiftRule | null;
  onClose: () => void;
  onSubmit: (id: string, data: {
    ruleType?: RuleType;
    timeConditions?: TimeCondition[];
  }) => Promise<boolean>;
  isLoading: boolean;
  error: string | null;
}

/**
 * 医師シフトルール編集モーダル
 *
 * - 医師情報表示（読取専用）
 * - ルールタイプ編集
 * - 時間条件編集
 * - プレビュー表示
 */
const DoctorShiftRuleEditModal: React.FC<DoctorShiftRuleEditModalProps> = memo(({
  open,
  rule,
  onClose,
  onSubmit,
  isLoading,
  error
}) => {
  const [ruleType, setRuleType] = useState<RuleType>('assignable');
  const [timeConditions, setTimeConditions] = useState<TimeCondition[]>([]);

  // ルールが変更されたら状態を更新
  useEffect(() => {
    if (rule) {
      setRuleType(rule.ruleType);
      setTimeConditions([...rule.timeConditions]);
    }
  }, [rule]);

  const handleClose = useCallback(() => {
    if (!isLoading) {
      onClose();
    }
  }, [isLoading, onClose]);

  const handleSubmit = useCallback(async () => {
    if (!rule) return;

    await onSubmit(rule.id, {
      ruleType,
      timeConditions
    });
  }, [rule, ruleType, timeConditions, onSubmit]);

  if (!rule) {
    return null;
  }

  const isValid = timeConditions.length > 0;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { maxHeight: '90vh' }
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <EditIcon color="primary" />
          <Typography variant="h6">
            シフトルールを編集
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* 医師情報（読取専用） */}
        <Paper variant="outlined" sx={{ p: 2, mb: 3, bgcolor: 'grey.50' }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            対象医師
          </Typography>
          <Typography variant="body1" fontWeight={500}>
            {rule.doctorFullName}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            医師コード: {rule.doctorCode}
          </Typography>
        </Paper>

        {/* 時間条件編集 */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" fontWeight={500} sx={{ mb: 1 }}>
            ルール設定
          </Typography>
          <TimeConditionForm
            conditions={timeConditions}
            ruleType={ruleType}
            onChange={setTimeConditions}
            onRuleTypeChange={setRuleType}
            disabled={isLoading}
          />
        </Box>

        {/* プレビュー */}
        <Paper variant="outlined" sx={{ p: 2, bgcolor: 'primary.50' }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            プレビュー
          </Typography>
          <Typography variant="body2" color="primary.main" fontWeight={500}>
            {RULE_TYPE_LABELS[ruleType]}
          </Typography>
          {timeConditions.map((tc, index) => (
            <Typography key={index} variant="body2">
              {formatTimeCondition(tc)}
            </Typography>
          ))}
        </Paper>
      </DialogContent>

      <DialogActions>
        <Button
          onClick={handleClose}
          disabled={isLoading}
        >
          キャンセル
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!isValid || isLoading}
          startIcon={isLoading ? <CircularProgress size={20} /> : <EditIcon />}
        >
          {isLoading ? '更新中...' : '更新'}
        </Button>
      </DialogActions>
    </Dialog>
  );
});

DoctorShiftRuleEditModal.displayName = 'DoctorShiftRuleEditModal';

export default DoctorShiftRuleEditModal;
